import { AxiosPromise, AxiosResponse, AxiosRequestConfig } from 'axios';
import { IToken, IUserData, IAuthorizeApprovalParams } from '@app/models';
import { apiService, vaultsService } from '@app/services';
import qs from 'qs';
import { basicService } from './basic.service';
import { store, onboardingStorage, expirationStorage, refreshTokenStorage, localStore, getCookieValue, signedInCookie, toBoolean } from '@app/helpers';
import { currentUser } from '@app/store/actions';
import { PASSLY_PORTAL_CLIENT_ID } from '@app/constants';

export const AUTHORIZE_TOKEN_API_PATH = '/authorize/token';
export const SIGNOUT_API_PATH = 'api/profile/signout';

class AuthService {
    readonly apiBase: string = '/api/authorize';
    private isAlreadyFetchingAccessToken: boolean = false;
    private subscribers: any[] = [];

    signin(username: string, password: string): AxiosPromise<IToken> {
        const data = qs.stringify({
            grant_type: 'password',
            client_id: PASSLY_PORTAL_CLIENT_ID,
            username: username,
            password: password
        });

        const config: AxiosRequestConfig = {
            local: true
        };

        return apiService.post(AUTHORIZE_TOKEN_API_PATH, data, config).then((response: AxiosResponse<IToken>) => {
            return response;
        });
    }

    signOut(): AxiosPromise {
        // Success or fail doesn't matter
        const config: AxiosRequestConfig = {
            local: true
        };

        return apiService.post('api/profile/signout', {}, config);
    }

    signInKaseyaOne(username?: string, password?: string): AxiosPromise<IToken> {
        const data: any = {
            grant_type: 'k1',
            client_id: PASSLY_PORTAL_CLIENT_ID
        };

        if (username && password) {
            data.username = username;
            data.password = password;
        }

        const config: AxiosRequestConfig = {
            local: true
        };

        return apiService.post(AUTHORIZE_TOKEN_API_PATH, qs.stringify(data), config).then((response: AxiosResponse<IToken>) => {
            return response;
        });
    }

    onboard(token: string): AxiosPromise {
        basicService.clearUserLocalStorageData();

        const data = qs.stringify({
            grant_type: 'onboarding',
            client_id: PASSLY_PORTAL_CLIENT_ID,
            session: token
        });

        return apiService.post(AUTHORIZE_TOKEN_API_PATH, data, { local: true }).then((response: AxiosResponse<IToken>) => {
            return response;
        });
    }

    signInOtp(contextToken: string, passcode: string): AxiosPromise {
        basicService.clearUserLocalStorageData();

        return apiService
            .post(
                AUTHORIZE_TOKEN_API_PATH,
                qs.stringify({
                    grant_type: 'otp',
                    client_id: PASSLY_PORTAL_CLIENT_ID,
                    context_token: contextToken,
                    passcode
                }),
                { local: true }
            )
            .then((response: AxiosResponse<IToken>) => {
                return response;
            });
    }

    signInPush(contextToken: string): AxiosPromise {
        basicService.clearUserLocalStorageData();

        return apiService
            .post(
                AUTHORIZE_TOKEN_API_PATH,
                qs.stringify({
                    grant_type: 'push',
                    client_id: PASSLY_PORTAL_CLIENT_ID,
                    context_token: contextToken,
                    timeout: 180
                }),
                { local: true }
            )
            .then((response: AxiosResponse<IToken>) => {
                return response;
            });
    }

    signInU2f(contextToken: string, u2fSignature: string): AxiosPromise {
        basicService.clearUserLocalStorageData();

        return apiService
            .post(
                AUTHORIZE_TOKEN_API_PATH,
                qs.stringify({
                    grant_type: 'u2f',
                    client_id: PASSLY_PORTAL_CLIENT_ID,
                    context_token: contextToken,
                    u2f_signature: u2fSignature
                }),
                { local: true }
            )
            .then((response: AxiosResponse<IToken>) => {
                return response;
            });
    }

    signInK1(accessToken: string): AxiosPromise {
        basicService.clearUserLocalStorageData();

        return apiService
            .post(
                AUTHORIZE_TOKEN_API_PATH,
                qs.stringify({
                    grant_type: 'access_token',
                    client_id: PASSLY_PORTAL_CLIENT_ID,
                    access_token: accessToken
                }),
                { local: true }
            )
            .then((response: AxiosResponse<IToken>) => {
                return response;
            });
    }

    refreshToken(): AxiosPromise<IToken> {
        // refresh token is in cookie
        const data = qs.stringify({
            grant_type: 'refresh_token',
            client_id: PASSLY_PORTAL_CLIENT_ID,
            refresh_token: ''
        });

        const config: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            local: true
        };

        // if this fails, nothing we can do just
        // make the request with the old token
        return apiService.post(AUTHORIZE_TOKEN_API_PATH, data, config).then((response: AxiosResponse<IToken>) => {
            return response;
        });
    }

    elevate(userId: string, methods: string) {
        const data = qs.stringify({
            grant_type: 'elevate',
            client_id: PASSLY_PORTAL_CLIENT_ID,
            userUuid: userId,
            methods: methods
        });

        const config: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            local: true
        };

        return apiService.post(AUTHORIZE_TOKEN_API_PATH, data, config).catch();
    }

    setEphemeral(ephemeral: boolean) {
         // ephemeral session should always be set in local storage
        if (!ephemeral) {
            localStore.setItem('ephemeral_session', 'false');
            return;
        }

        localStore.setItem('ephemeral_session', 'true');
    }

    handleLocalStorage(token: IToken) {
        this.setEphemeral(token.ephemeral ? token.ephemeral : false);
        token.onboarding ? this.setOnboarding('true') : this.clearOnboarding();
        this.setExpiration(token.exp);
        if (token.refresh_token) {
            this.setRefreshToken('1');
        }
    }

    setOnboarding(onboarding: string): void {
        this.localStorage().setItem(onboardingStorage, onboarding);
    }

    getOnboarding(): string | null {
        return this.localStorage().getItem(onboardingStorage);
    }

    clearOnboarding(): void {
        this.localStorage().removeItem(onboardingStorage);
    }

    setExpiration(expiration: string): void {
        this.localStorage().setItem(expirationStorage, expiration);
    }

    getExpiration(): number {
        const expiration = this.localStorage().getItem(expirationStorage);
        if (expiration === null) {
            return 0;
        } else {
            return parseInt(expiration);
        }
    }

    clearExpiration(): void {
        this.localStorage().removeItem(expirationStorage);
    }

    isSignedIn(): boolean {
        const tokenOwner = decodeURIComponent(getCookieValue('token_owner'));
        let tokenOwnerMatches = tokenOwner === window.location.host;

        // used only when running dev build using the command:
        // > npm start
        if (process.env.NODE_ENV === 'development') {
            if (process.env.REACT_APP_PASSLY_LOCAL_FORWARDED_HOST) {
                tokenOwnerMatches = new URL(process.env.REACT_APP_PASSLY_LOCAL_FORWARDED_HOST).host === tokenOwner;
            }
            else if (process.env.REACT_APP_PASSLY_API_HOST) {
                let host = new URL(process.env.REACT_APP_PASSLY_API_HOST).host;
                tokenOwnerMatches = host === tokenOwner;
            }
            else {
                throw 'Check REACT_APP_PASSLY_API_HOST is set in .env.development file';
            }
        }

        return  tokenOwnerMatches && toBoolean(getCookieValue(signedInCookie));
    }

    setRefreshToken(refreshToken: string) {
        this.localStorage().setItem(refreshTokenStorage, refreshToken);
    }

    getRefreshToken() {
        return this.localStorage().getItem(refreshTokenStorage);
    }

    setUserData(user: IUserData) {
        this.localStorage().setItem('kaseya-user', JSON.stringify(user));
    }

    getUserData() {
        const userData = this.localStorage().getItem('kaseya-user');

        if (userData !== null) {
            return JSON.parse(userData);
        }
    }

    tokenExpiresSoonish() {
        const storedExpiration = this.getExpiration();

        if (storedExpiration === 0) {
            return true;
        }

        let exp = new Date(0);
        exp.setUTCSeconds(storedExpiration);

        let now = new Date();
        now.setSeconds(now.getSeconds() + 180);

        return !(exp.valueOf() > now.valueOf());
    }

    getNewAccessToken(callback?: Function) {
        authService
            .refreshToken()
            .then((response: AxiosResponse) => {
                const newToken: IToken = response.data;
                const userData: IUserData = this.modelUserData(newToken);
                this.handleLocalStorage(newToken);
                this.setUserData(userData);
                store.dispatch(currentUser(userData));
                this.isAlreadyFetchingAccessToken = false;
                this.onAccessTokenFetched('1');
            })
            .then(() => {
                if (callback !== undefined) callback();
            })
            .catch(() => {
                this.isAlreadyFetchingAccessToken = false;
                this.onAccessTokenFetched('1');
            });
    }

    getNewAccessTokenOnRequest() {
        if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true;

            this.getNewAccessToken(() => vaultsService.updateVaultsPermissions());
        }

        return new Promise<void>((resolve) => {
            this.addSubscriber(() => {
                resolve();
            });
        });
    }

    modelUserData(data: IToken): IUserData {
        let userData: IUserData = {
            username: data.nameid,
            email: data.email,
            displayName: data.dn,
            userUuid: data.uid,
            cultureCode: data.cul,
            onboarding: data.onboarding,
            tid: data.tid,
            oid: data.oid,
            ephemeral: data.ephemeral,
            uid: data.uid,
            scopes: data.scopes,
            vaults: data.vaults,
            permissions: new Array<string>(),
            orgPermissions: {},
            vaultPermissions: {},
            vaultPermNames: new Array<string>()
        };

        this.setAppPermissions(data.scopes, userData);
        this.setVaultPermissions(data.vaults, userData);

        return userData;
    }

    // When switching from the browser extension to the website, kaseya-user in local storage may not exist.
    // In this case we need to set the user data returned from the shell controller.
    setUserDataFromShell(claims: any) {
        let claimsToken = claims as IToken;

        if (claimsToken.exp && claimsToken.exp !== undefined) {
            this.setExpiration(claimsToken.exp);
        }

        if (claims.nameid !== undefined) {
            let userData: IUserData = this.modelUserData(claims as IToken);

            this.setUserData(userData);
            store.dispatch(currentUser(userData));
        }
    }

    setAppPermissions(scopes: string, data: IUserData) {
        if (!scopes) {
            return;
        }

        let permsRaw = scopes.split(' ');

        let permStructure: { [permKey: string]: string[] } = {};
        let permNames = new Array<string>();

        permsRaw.forEach(function(raw) {
            let permOrg = raw.split(':');

            let orgPermNames = permOrg[1].split(',');

            permStructure[permOrg[0]] = orgPermNames;

            orgPermNames.forEach(function (p) {
                permNames.push(p);
            });
        });

        data.orgPermissions = permStructure;
        data.permissions = permNames.filter(this.uniqueValues);
    }

    setVaultPermissions(vaults: string, data: IUserData) {
        if (!vaults) {
            return;
        }

        let permsRaw = vaults.split(' ');

        let permStructure: { [permKey: string]: string[] } = {};
        let permNames = new Array<string>();

        permsRaw.forEach(function(raw) {
            let permVault = raw.split(':');

            let vaultPermNames = permVault[1].split(',');

            permStructure[permVault[0]] = vaultPermNames;

            vaultPermNames.forEach(function (p) {
                permNames.push(p);
            });
        });

        data.vaultPermissions = permStructure;
        data.vaultPermNames = permNames.filter(this.uniqueValues);
    }

    uniqueValues(value: any, index: number, self: any[]): boolean {
        return self.indexOf(value) === index;
    }

    authorize(params: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.get(`${this.apiBase}${params}`,
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                local: true,
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    authorizeApproval(params: string, data: IAuthorizeApprovalParams, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.post(`${this.apiBase}${params}`,
            {
                approved: data.approved,
                rememberDecision: data.rememberDecision
            },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                local: true,
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    private localStorage() {
        return basicService.getStorage();
    }

    private onAccessTokenFetched(access_token: string) {
        this.subscribers = this.subscribers.filter((callback: any) => callback(access_token));
    }

    private addSubscriber(callback: any) {
        this.subscribers.push(callback);
    }
}

export const authService = new AuthService();
